import { useState, useEffect, useContext } from "react";
import { HotelContext } from '../../../context/hotel';
import { CheckInContext } from '../../../context/checkin';
import { T, TF } from "react-translator-component";
import Screen from '../../../components/Screen';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { getCDNImage } from '../../../globals';
import { useNavigate } from 'react-router-dom';
import './WelcomeKiosk.css';
import { UserContext } from '../../../context/user';
import { i18nValue, reservationDisplayCode } from '../../../functions';

const WelcomeKiosk = () => {
    const { language } = useContext(UserContext);
    const navigate = useNavigate();
    const { data: hotel, getUrlPath } = useContext(HotelContext);
    const { data: checkInData } = useContext(CheckInContext);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [secondsRemaining, setSecondsRemaining] = useState(90);
    
    useEffect(() => {
        let url = (window.location.origin).replace('kiosk', 'hotel');
        let pwaUrl = `${url}${getUrlPath(`/r/${checkInData?.securityCode}`)}`;
        if (pwaUrl) {
            // Generamos el código QR con la URL de la PWA
            setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${pwaUrl}`);
        }
    }, [hotel?.code]);

    useEffect(() => {
        // Limpiar localStorage para el siguiente huésped
        setTimeout(() => {
            localStorage.clear();
        }, 9000);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setSecondsRemaining(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    // Redireccionar a la pantalla de identificación para el siguiente huésped
                    navigate(getUrlPath(`/checkin/identify`));
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        
        return () => clearInterval(interval);
    }, [hotel, getUrlPath, navigate]);

    const formatDate = (iso) => {
        if (!iso) return '-';
        try {
            const d = new Date(iso);
            return d.toLocaleDateString(language, { year: 'numeric', month: '2-digit', day: '2-digit' });
        } catch {
            return String(iso);
        }
    };

    const goToIdentify = () => {
        localStorage.clear();
        navigate(`/${hotel?.code}/checkin/identify`);
    };

    return (
        <Screen
            section={'kiosk/welcome-kiosk'}
            title={'Welcome'}
            buttonBack={false}
            showBackButton={false}
            hideHeader={true}
            backUrl={`/${hotel?.code}/checkin/identify`}
            classes="kiosk-welcome-screen"
        >
            <div className="kiosk-welcome-background"></div>
            <div className="welcome-kiosk-container">
                {/* Hotel Branding */}
                <div className="welcome-kiosk-branding">
                    {hotel?.logo && (
                        <img 
                            src={getCDNImage(hotel.logo)} 
                            alt={hotel.name}
                            className="hotel-logo"
                        />
                    )}
                    <h2 className="hotel-title">{hotel?.name}</h2>
                </div>

                {/* Header */}
                <div className="welcome-kiosk-header">
                    <div className="welcome-kiosk-header-content">
                        <FontAwesomeIcon icon={faCheckCircle} className="welcome-kiosk-icon" />
                        <div className="welcome-kiosk-text">
                            <h1 className="welcome-kiosk-title">
                                ¡{T("general.identify-welcome")}, {checkInData?.passengers?.[0]?.name ? ` ${checkInData?.passengers?.[0]?.name}` : ''}!
                            </h1>
                            <p className="welcome-kiosk-subtitle">
                                {T("kiosk.welcome.subtitle")}
                            </p>
                        </div>
                        <div className="welcome-kiosk-code">
                            <span>{reservationDisplayCode(checkInData)}</span>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="welcome-kiosk-content">
                    {/* Room Information */}
                    <div className="welcome-kiosk-section room-info-section">
                        <div className="dates-row">
                            <div className="date-item">
                                <div className="date-texts">
                                    <span className="date-label">{T("kiosk.room")}</span>
                                    <span className="date-value">{checkInData?.Room?.code || '-'}</span>
                                </div>
                            </div>
                            <div className="dates-divider" />
                            <div className="date-item">
                                <div className="date-texts">
                                    <span className="date-label">{T("kiosk.checkin")}</span>
                                    <span className="date-value">{formatDate(checkInData?.dateStart)}</span>
                                </div>
                            </div>
                            <div className="dates-divider" />
                            <div className="date-item">
                                <div className="date-texts">
                                    <span className="date-label">{T("kiosk.checkout")}</span>
                                    <span className="date-value">{formatDate(checkInData?.dateEnd)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="welcome-kiosk-flex-container">
                        {/* Room Category */}
                        <div className="room-category-card">
                            {checkInData?.RoomCategory?.photo && (
                                <img 
                                    src={getCDNImage(checkInData?.RoomCategory?.photo)} 
                                    alt={checkInData?.RoomCategory?.name}
                                    className="room-category-image"
                                />
                            )}
                            <h3 className="room-category-name">
                                <span className="room-category-name text-left">{i18nValue(checkInData?.RoomCategory, 'name', language)}</span>
                            </h3>
                        </div>

                        {/* QR Code */}
                        <div className="welcome-kiosk-section qr-section">
                            <h2 className="section-title mb-3">{T("kiosk.download_app")}</h2>
                            <div className="qr-container">
                                {qrCodeUrl ? (
                                    <img 
                                        src={qrCodeUrl} 
                                        alt="QR Code"
                                        className="qr-code"
                                    />
                                ) : (
                                    <div className="qr-placeholder">
                                        <p>{T("kiosk.generating_qr")}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Redirect Warning */}
                <div className="welcome-kiosk-redirect-warning-container">
                    <div className="welcome-kiosk-redirect-warning">
                        <p>{TF("checkin.redirecting", secondsRemaining)}</p>
                    </div>
                    <div className="welcome-kiosk-footer">
                    <button className="welcome-kiosk-button" onClick={goToIdentify}>
                        {T("kiosk.go_to_identify")}
                    </button>
                </div>
            </div>
            </div>

        </Screen>
    );
};

export default WelcomeKiosk;